import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { DocumentAccessCredentialsModel, DocumentModel } from '@data-layer/models/documents';
import {
  enumDocumentRelatedEntityType,
  enumDocumentRootEntityType,
  enumDocumentState,
  enumDocumentType,
} from '@data-layer/enums/documents';
import {
  catchError,
  debounceTime,
  defaultIfEmpty,
  delay,
  delayWhen,
  filter,
  finalize,
  first,
  map,
  retryWhen,
  switchMap,
  take,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { DateHelper } from '@helpers/date.helper';
import { EMPTY, forkJoin, Observable, of, ReplaySubject, Subject, timer } from 'rxjs';
import { CompaniesService } from '@services/companies.service';
import { DocumentsService } from '@services/documents.service';
import { DependenciesService } from '@services/dependencies.service';
import { DependencyModel } from '@data-layer/models/dependencies';
import { enumDependencyState } from '@data-layer/enums/dependencies';
import { CompanyBondModel, CompanyControllingPersonModel, CompanyModel } from '@data-layer/models/companies';
import { HttpErrorResponse } from '@angular/common/http';
import { ManagementEntityEventModel } from '@data-layer/models/management';
import { DateTimezoneService } from '@services/date-timezone.service';
import { AbstractControl, UntypedFormArray, UntypedFormGroup, Validators } from '@angular/forms';
import { UserModel } from '@data-layer/models/user';
import { IorContactMatchHelper, IorContactValues } from '@helpers/ior-contact-match.helper';
import { enumCompanyPlacement } from '@enum/enumCompanyPlacement';
import {
  enumBondRequestState,
  enumBondType,
  enumCbpAssignedNumberStatus,
  EnumHintKey,
  enumIdentifierQualifier,
  enumValidationMessageSeverity,
} from '@data-layer/enums/common';
import { enumGATagEventAction } from '@enum/enumGATagEventAction';
import { GoogleAnalyticsService } from '@services/google-analytics.service';
import { enumShippingRole, enumUserBetaFlag } from '@app/data-layer/enums/user';
import { enumCompanyBusinessType, enumCompanyPartnershipType, enumCompanyPaymentType } from '@app/data-layer/enums/companies';
import { AddressModel, PaginatedResultModel, ValidationMessageModel } from '@data-layer/models/common';
import {
  BillsRepository,
  BondRequestRepository,
  CompaniesRepository,
  CompanyBondsRepository,
  CompanyLogoRepository,
  DocumentsRepository,
  PaymentsRepository,
} from '@data-layer/repositories';
import { GenericService } from '@services/generic.service';
import { AppService } from '@app/app.service';
import { UserBetaFlagModel } from '@data-layer/models/user';
import { DEFAULT_MAX_POLLING_RETRY, DEFAULT_POLLING_TIMEOUT, DEFAULT_STATUS_PING_TIME, USA_COUNTRY_CODE } from '@constants/constants';
import {
  controllingPersonRoleKey,
  isPartnership,
  isStateOfIncorporationApplicable,
  minControllingPersons,
  requiresControllingPersons,
  requiresPhotoId,
} from '@constants/business-types';
import { requiresSs4Document } from '@constants/company-documents';
import { identifierSSNValidator } from '@helpers/custom-validator';
import { createControllingPersonFormGroup } from '@helpers/controlling-person-form.helper';
import { flatten, omit } from 'lodash-es';
import { UtilsHelper } from '@helpers/utils-helper';
import { ViewportScroller } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { BillModel, PaymentMethodModel } from '@data-layer/models/payments';
import { enumPaymentProvider, enumPaymentState } from '@data-layer/enums/payments';
import { OrganizationsRepository } from '@app/data-layer/repositories/organizations.repository';
import { OrganizationMemberModel } from '@app/data-layer/models/organizations/organization-member-model';
import { enumOrganizationRole } from '@app/data-layer/enums/organizations';
import { OrganizationModel } from '@app/data-layer/models/organizations/organization-model';
import { FormHelper } from '@helpers/form.helper';
import { isBannedIorAddress } from '@helpers/banned-address.helper';
import { MissingFieldsComponent } from '@shared/missing-fields/components';
import { TranslateService } from '@ngx-translate/core';
import { CompanyPowerAttorneyComponent } from '../company-power-attorney/company-power-attorney.component';
import { ControllingPersonScanBufferEntry } from '../company-controlling-persons-form/company-controlling-persons-form.component';
import { CompanyEmployeeStatusComponent } from '../company-employee-status/company-employee-status.component';
import { IorMatchGateService } from '@services/ior-match-gate.service';
import { RequestLoaderService } from '@services/request-loader.service';

interface EditableBlocksModel {
  isEditBusinessInfo: boolean;
  isEditPwa: boolean;
  isEditDutiesPayment: boolean;
}

@Component({
  selector: 'edit-company',
  templateUrl: './edit-company.component.html',
  styleUrls: ['./edit-company.component.scss'],
})
export class EditCompanyComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(MissingFieldsComponent, { static: false }) public missingFieldModal: MissingFieldsComponent;
  @ViewChild(CompanyPowerAttorneyComponent) public poaComponent: CompanyPowerAttorneyComponent;
  @ViewChild(CompanyEmployeeStatusComponent) public employeeStatusComponent: CompanyEmployeeStatusComponent;

  //TODO: remove @Input company and logic after new bonds
  @Input() public organization: OrganizationModel;
  @Input() public company: CompanyModel;

  @Input() public formGroup: any;
  @Input() public isEditView: boolean = false;
  @Input() public isFirstCompany: boolean = false;
  @Input() public isEditViewFromBondsPage: boolean = false;
  @Input() public isPayPalPaymentTokenLoading: boolean = false;
  @Input() public isDisableCountry: boolean = true;
  @Output() public updateCompanySource$: EventEmitter<UntypedFormGroup> = new EventEmitter<UntypedFormGroup>();
  @Output() public createCompanySource$: EventEmitter<number> = new EventEmitter<number>();
  @Output() public resetFormSource$: EventEmitter<void> = new EventEmitter<void>();
  @Output() public deletePayment: EventEmitter<enumPaymentProvider> = new EventEmitter<enumPaymentProvider>();
  @Output() public deleteCompanySource$: EventEmitter<void> = new EventEmitter<void>();
  @Output() public paypalPaymentToken: EventEmitter<void> = new EventEmitter<void>();

  public isHasOnboardingRedesign: boolean = false;
  public isPoaDocusignEnabled: boolean = false;
  public enumDocument: typeof enumDocumentType = enumDocumentType;
  public enumPlacement: typeof enumCompanyPlacement = enumCompanyPlacement;
  public enumHintKey: typeof EnumHintKey = EnumHintKey;

  public enumShippingRole: typeof enumShippingRole = enumShippingRole;
  public enumOrganizationMemberRole: typeof enumOrganizationRole = enumOrganizationRole;
  public enumCompanyBusinessType: typeof enumCompanyBusinessType = enumCompanyBusinessType;
  public enumDependencyState: typeof enumDependencyState = enumDependencyState;

  public companyDocuments: DocumentModel[] = [];
  public lastDependency: DependencyModel;
  public organizationMembers: OrganizationMemberModel[];
  public lastDependencyErrorCodes: string[];

  public uploadDocuments: Array<{ docType: number; file: File }> = [];

  // #13269 — controlling persons: type-112 scans of saved people plus the in-memory files picked before "Save changes"
  public controllingPersonDocuments: DocumentModel[] = [];
  public controllingPersonScans: Map<AbstractControl, ControllingPersonScanBufferEntry> = new Map();
  public controllingPersonScanUrls: Map<number, string> = new Map();
  public controllingPersonUploadingGroups: Set<AbstractControl> = new Set();
  public controllingPersonScanErrors: Map<AbstractControl, string> = new Map();

  public delayedUploadFile: File;
  public photoIdDocument: DocumentModel = null;
  public photoIdDownloadUrl: string = null;
  public photoIdIsImage: boolean = false;
  public isLoadingPhotoId: boolean = false;
  // #13356 — SS-4 (IRS EIN assignment letter), required for EIN-registered companies
  public ss4Document: DocumentModel = null;
  public ss4DownloadUrl: string = null;
  public ss4IsImage: boolean = false;
  public isLoadingSs4: boolean = false;
  public isLoadingUpsert: boolean;
  public isLoadingCompanyPoa: boolean = false;
  public isLoadingCompanyPun: boolean = false;
  public showPUNExampleModal: boolean = false;
  public showPUNDocValidationModal: boolean = false;
  public showPUNCheckboxValidationModal: boolean = false;
  public showBannedAddressModal: boolean = false;
  public showFormBlockSavedModal: boolean = false;
  // public isAllowedRegistrationInitialState: boolean = false;
  public isRegistrationInProcess: boolean = false;

  public editableBlocksState: EditableBlocksModel = {
    isEditBusinessInfo: false,
    isEditPwa: false,
    isEditDutiesPayment: false,
  };

  public companyId: number;
  public placement: enumCompanyPlacement;

  //TODO: remove continuousBonds,singleBonds,enumBondRequestState and logic after new bonds
  public continuousBonds: CompanyBondModel[] = [];
  public singleBonds: CompanyBondModel[] = [];
  public enumBondRequestState: typeof enumBondRequestState = enumBondRequestState;
  public enumUserBetaFlag: typeof enumUserBetaFlag = enumUserBetaFlag;
  public isTablet$: Observable<boolean>;
  public isDesktop$: Observable<boolean>;

  public isLoadingPaymentMethods: boolean;
  public allPaymentMethods: PaymentMethodModel[] = [];
  public paymentMethods: PaymentMethodModel[] = [];
  public isLoadingPaymentForm: boolean;

  public monthlyBillList: BillModel[] = [];

  private pingBondsStatus$: ReplaySubject<boolean> = new ReplaySubject<boolean>();
  private destroy$: ReplaySubject<boolean> = new ReplaySubject();
  // #13282 — lifetime of the subscriptions bound to the current formGroup instance (see initFormBoundLogic)
  private formDestroy$: Subject<boolean> = new Subject<boolean>();

  public get identifierQualifier(): enumIdentifierQualifier {
    return this.formGroup?.get('identifierQualifier')?.value;
  }

  public get showUserPhone(): boolean {
    return this.placement !== enumCompanyPlacement.COMPANY_PAGE && !this.formGroup.get('isImporterOfRecord')?.value;
  }

  public get canDeleteCompany(): boolean {
    return !!this.company?.canDelete && (this.organization?.companies?.length ?? 0) > 1;
  }

  public get showEmployeeStatus(): boolean {
    if (!this.organization) return false;
    const companiesCount = this.organization.companies?.length ?? 0;
    const isNewCompanyCreation = !this.formGroup?.get('id')?.value;
    if (companiesCount + (isNewCompanyCreation ? 1 : 0) > 1) return false;

    if (!this.organization.businessName || !this.organization.identifier || !this.organization.identifierQualifier) {
      return true;
    }

    if (this.organization.identifierQualifier === enumIdentifierQualifier.SSN && !this.organization.dateOfBirth) {
      return true;
    }

    return false;
  }

  public get isPoaFieldsComplete(): boolean {
    const isFormValid = this.formGroup.valid;
    const isUsa = this.formGroup.get('isUnitedStates')?.value;
    const identifierQualifier = this.formGroup.get('identifierQualifier')?.value;
    const isAllowedRegistration = this.formGroup.get('isAllowedRegistration')?.value;

    // Check IOR contact information is complete (required for digital POA).
    // #13212 — also block when IOR email/phone match the current user. Computed here (not via the
    // child IOR form, which is NOT mounted on the read-only company-view page where this gate runs).
    const isIorContactComplete = this.isIorContactInformationComplete() && !this.isIorContactMatchingUser();

    // #13188 — Individual and Sole Proprietor business types require a Photo ID document
    const businessType = this.formGroup.get('businessType')?.value;
    if (requiresPhotoId(businessType)) {
      const hasPhotoId =
        !!this.photoIdDocument ||
        this.uploadDocuments.some((doc: { docType: number; file: File }) => doc.docType === enumDocumentType.ID_DOCUMENT);
      if (!hasPhotoId) {
        return false;
      }
    }

    // #13356 — EIN-registered companies must have an SS-4. Skipped once the POA is signed/paid
    // (grandfathered companies are never asked for it retroactively). The isUsa condition mirrors
    // company-business-info-form.isSs4Visible: gating a company that never sees the block is a dead end.
    if (isUsa && requiresSs4Document(identifierQualifier) && !this.isPoaGrandfathered) {
      const hasSs4 =
        !!this.ss4Document || this.uploadDocuments.some((doc: { docType: number; file: File }) => doc.docType === enumDocumentType.SS4);
      if (!hasSs4) {
        return false;
      }
    }

    // #13269 — Corporation/LLC/Partnership require their controlling persons to be complete,
    // each with a State ID scan (uploaded or buffered), and Partnership also needs a sub-type
    if (!this.isControllingPersonsComplete(businessType)) {
      return false;
    }

    // If USA and not ANI (i.e., EIN or SSN), also require isAllowedRegistration to be checked
    if (isUsa && identifierQualifier !== enumIdentifierQualifier.ANI) {
      return isFormValid && !!isAllowedRegistration && isIorContactComplete;
    }

    return isFormValid && isIorContactComplete;
  }

  public get companyThumbnailUrl(): string | null {
    if (this.isPhotoIdBusinessType) {
      return this.photoIdIsImage ? this.photoIdDownloadUrl : null;
    }
    return this.formGroup.get('logoUri')?.value || null;
  }

  // #13356 — read from the mounted company-power-attorney (both view + edit render it) to avoid a
  // duplicate digital-signs fetch, exactly as isIorContactMatchingUser does.
  public get isPoaGrandfathered(): boolean {
    return !!this.poaComponent && this.poaComponent.isPoaGrandfathered;
  }

  // #13188 — Individual and Sole Proprietor use the Photo ID uploader (and its image as thumbnail)
  public get isPhotoIdBusinessType(): boolean {
    return requiresPhotoId(this.formGroup.get('businessType')?.value);
  }

  // #13188 — read-only State of Incorporation label for the collapsed business-info summary card
  public get stateOfIncorporationText(): string {
    const state = this.formGroup.get('stateOfIncorporation')?.value;
    return state ? `${state.code} ${state.description}` : '';
  }

  public onPhotoIdImageError(): void {
    this.photoIdDownloadUrl = null;
    this.cd.markForCheck();
  }

  private isIorContactInformationComplete(): boolean {
    const iorGroup = this.formGroup.get('importerOfRecordContactInformation');
    if (!iorGroup) {
      return false;
    }

    const firstName = iorGroup.get('firstName')?.value;
    const lastName = iorGroup.get('lastName')?.value;
    const phoneNumber = iorGroup.get('phoneNumber')?.value;
    const emailAddress = iorGroup.get('emailAddress')?.value;

    // All fields must be non-empty after trimming. For phone/email also require control validity
    // when enabled (#13175: catches the iorMatchesUser error). When disabled (checkbox checked)
    // skip validation since user data is trusted.
    const isFirstNameValid = !!firstName?.trim();
    const isLastNameValid = !!lastName?.trim();
    const phoneControl: AbstractControl | null = iorGroup.get('phoneNumber');
    const isPhoneNumberValid = !!phoneNumber?.trim() && (phoneControl?.disabled || phoneControl?.valid);

    const emailControl = iorGroup.get('emailAddress');
    const isEmailAddressValid = !!emailAddress?.trim() && (emailControl?.disabled || emailControl?.valid);

    return isFirstNameValid && isLastNameValid && isPhoneNumberValid && isEmailAddressValid;
  }

  // #13212 — IOR email/phone equal the current user's while "I am the IOR" is unchecked → must
  // block the digital-sign POA button. Computed here (independent of the child IOR form, which
  // isn't mounted on the read-only company-view page). Uses getRawValue to include disabled fields.
  private isIorContactMatchingUser(): boolean {
    if (this.formGroup.get('isImporterOfRecord')?.value) {
      return false;
    }
    // #13212 — respect grandfathering (POA already signed/paid). Read from the mounted
    // company-power-attorney (both view + edit render it) to avoid a duplicate digital-signs fetch.
    if (this.isPoaGrandfathered) {
      return false;
    }
    const iorGroup: UntypedFormGroup = this.formGroup.get('importerOfRecordContactInformation') as UntypedFormGroup;
    if (!iorGroup) {
      return false;
    }
    const ior: IorContactValues = iorGroup.getRawValue();
    const user: UserModel = this.companiesService.getUser();
    return IorContactMatchHelper.emailMatchesUser(ior, user) || IorContactMatchHelper.phoneMatchesUser(ior, user);
  }

  constructor(
    private router: Router,
    private appService: AppService,
    private dependenciesService: DependenciesService,
    private companiesService: CompaniesService,
    private companiesRepository: CompaniesRepository,
    private organizationsRepository: OrganizationsRepository,
    private paymentsRepository: PaymentsRepository,
    private dateTimezoneService: DateTimezoneService,
    private fh: FormHelper,
    private cd: ChangeDetectorRef,
    private ga: GoogleAnalyticsService,
    private documentsService: DocumentsService,
    private documentsRepository: DocumentsRepository,
    private companyLogoRepository: CompanyLogoRepository,
    private genericService: GenericService,
    //TODO: remove companyBondsRepository,bondRequestRepository after new bonds
    private translateService: TranslateService,
    private companyBondsRepository: CompanyBondsRepository,
    private bondRequestRepository: BondRequestRepository,
    private viewportScroller: ViewportScroller,
    private activeRoute: ActivatedRoute,
    private billsRepository: BillsRepository,
    private iorMatchGate: IorMatchGateService,
    private requestLoaderService: RequestLoaderService,
  ) {}

  public ngOnInit(): void {
    this.isTablet$ = this.genericService.isTablet$;
    this.isDesktop$ = this.genericService.isDesktop$;
    this.placement = this.companiesService.placement;
    this.companyId = this.formGroup?.get('id').value;

    if (this.organization && this.isEditView) {
      this.organizationsRepository
        .getOrganizationMembers(this.organization.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe((organizationMembers) => {
          this.organizationMembers = organizationMembers;
        });
    }

    if (!!this.companyId) {
      this.getCompanyDocuments(true).pipe(takeUntil(this.destroy$)).subscribe();
      this.getCompanyDependencies();
    }

    // this.isAllowedRegistrationInitialState = this.formGroup.get('isAllowedRegistration')?.value;

    this.initFormBoundLogic();

    // #8632
    this.pingBondsStatus$
      .pipe(
        withLatestFrom(this.genericService.isDesktop$),
        filter(([val, isDesktop]: [boolean, boolean]) => !!val && !isDesktop),
        delay(DEFAULT_STATUS_PING_TIME),
        tap(() => this.getBonds(false)),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.pingBondsStatus$.next(true);
      });

    this.loadPaymentMethods();
    this.loadMonthlyBills();

    const params = this.activeRoute.snapshot.queryParams;
    const isEditViewOpen = !!params?.isEditViewOpen;
    const cbpRequest = !!params?.cbpRequest && params?.cbpRequest === this.companyId.toString();
    const requiresValidation = !!params?.requiresValidation && params?.requiresValidation === this.companyId.toString();

    if (isEditViewOpen) {
      this.openEditableBlock('isEditBusinessInfo');
    }

    if (requiresValidation && !this.companiesService.getCompanyById(this.company?.id)?.validationDate) {
      this.companiesService.updateCompanyValidationState(this.placement).pipe(takeUntil(this.destroy$)).subscribe();
      this.ga.companyValidate(this.gtmEventAction, String(this.companyId));
    }

    this.removeQueryParams([
      isEditViewOpen ? 'isEditViewOpen' : '',
      cbpRequest ? 'cbpRequest' : '',
      requiresValidation ? 'requiresValidation' : '',
    ]);

    this.companiesService.userBetaFlagsChange$
      .pipe(
        takeUntil(this.destroy$),
        filter((flags) => flags && flags.length > 0),
      )
      .subscribe((flags: UserBetaFlagModel[]) => {
        const tags = flags.map((flag: UserBetaFlagModel) => flag.tag);
        this.isHasOnboardingRedesign = tags.includes(enumUserBetaFlag.onboardingRedesign);
        this.isPoaDocusignEnabled = tags.includes(enumUserBetaFlag.poaDocusign);
        this.isDisableCountry = this.formGroup.get('identifierQualifier')?.value !== enumIdentifierQualifier.ANI;
        this.cd.detectChanges();
      });
  }

  // #13282 — every rule and subscription that is bound to the CURRENT formGroup instance.
  // The parent replaces the whole form on "Cancel" (revertFormToInitialState), so this must be
  // re-run for the new instance — otherwise the new form keeps neither its validators nor its
  // seeded controlling-person sections, and the old form's subscriptions leak.
  private initFormBoundLogic(): void {
    this.formDestroy$.next(true);
    // A replaced form drops the sections the buffered scans were keyed by
    this.controllingPersonScans.clear();
    this.controllingPersonScanErrors.clear();
    this.controllingPersonUploadingGroups.clear();

    this.formGroup
      .get('id')
      .valueChanges.pipe(takeUntil(this.formDestroy$))
      .subscribe((value: number) => {
        this.companyId = value;
      });

    if (!!this.formGroup.get('achIsProvided')) {
      this.formGroup
        .get('achIsProvided')
        .valueChanges.pipe(takeUntil(this.formDestroy$))
        .subscribe((radioValue: boolean) => {
          const punControl: AbstractControl = this.formGroup.get('payerUnitNumber');
          const agreementControl: AbstractControl = this.formGroup.get('authorizationAgreementAccepted');
          // #12900 — the ACH checkbox carries no validator: "YES and not checked" is gated by the dedicated
          // PUN checkbox modal in save(), never by the generic missing-fields popup
          if (!radioValue) {
            punControl.clearValidators();
            punControl.patchValue(null);
            agreementControl.patchValue(null);
          } else {
            punControl.setValidators([Validators.minLength(6), Validators.maxLength(6)]);
          }
          // validators changed → recompute status, otherwise a stale error survives the YES→NO switch
          punControl.updateValueAndValidity({ emitEvent: false });
        });
    }
    this.isUsaSubscription();
    this.isAllowCustomsSubscription();
    // #13188 — keep the State of Incorporation required validator in sync with the business type
    this.businessTypeSubscription();
    // #13520 — and with the address country (ANI unlocks it; a foreign address hides the field)
    this.addressCountrySubscription();

    // Apply validators based on initial values (for edit view)
    this.applyInitialValidators();
    // #13188 — set the State of Incorporation validator for the already-selected business type
    this.applyStateOfIncorporationValidator();
    // #13269 — seed the minimum controlling-person sections for the already-selected business type
    this.applyControllingPersonRules();

    this.formGroup
      .get('identifierQualifier')
      .valueChanges.pipe(takeUntil(this.formDestroy$))
      .subscribe((value) => {
        this.isDisableCountry = value !== enumIdentifierQualifier.ANI;
      });
  }

  public ngAfterViewInit(): void {
    this.activeRoute.fragment.pipe(first()).subscribe((fragment: string) => {
      if (!fragment) {
        return;
      }
      this.requestLoaderService.isLoadingAPI$
        .pipe(
          filter((isLoading: boolean) => !isLoading),
          debounceTime(150),
          first(),
          takeUntil(this.destroy$),
        )
        .subscribe(() => this.scrollToFragment(fragment));
    });
  }

  private findScrollableAncestor(el: HTMLElement): HTMLElement | null {
    let p: HTMLElement | null = el.parentElement;
    while (p) {
      const style = window.getComputedStyle(p);
      if (p.scrollHeight > p.clientHeight && (style.overflowY === 'auto' || style.overflowY === 'scroll')) {
        return p;
      }
      p = p.parentElement;
    }
    return null;
  }

  private scrollToFragment(fragment: string): void {
    const el: HTMLElement | null = document.getElementById(fragment);
    if (!el) {
      return;
    }
    const scrollable: HTMLElement | null = this.findScrollableAncestor(el);
    if (scrollable) {
      const elRect = el.getBoundingClientRect();
      const cRect = scrollable.getBoundingClientRect();
      const targetTop: number = scrollable.scrollTop + (elRect.top - cRect.top) - 75;
      scrollable.scrollTo({ top: targetTop, behavior: 'smooth' });
    } else {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.formDestroy$.next(true);
  }

  public openEditableBlock(variable: string): void {
    this.editableBlocksState[variable] = true;
    for (const key in this.editableBlocksState) {
      if (key !== variable) {
        this.cancelEditableBlock(key);
      }
    }
  }

  public cancelEditableBlock(variable: string): void {
    this.editableBlocksState[variable] = false;
    // #13175 — gate on `dirty` (real edits), not `touched`. Programmatic touched-marking
    // (e.g. input-phone on a pre-filled number, or the IOR match highlight) would otherwise
    // pop this "unsaved changes" modal the moment the user enters edit mode.
    if (this.formGroup.dirty) {
      this.showFormBlockSavedModal = true;
    }
  }

  public acceptEditableBlock(variable: string): void {
    this.editableBlocksState[variable] = false;
  }

  public discardChanges(): void {
    this.resetFormSource$.emit();
    this.showFormBlockSavedModal = false;
  }

  public saveChanges(): void {
    this.showFormBlockSavedModal = false;
    this.save(false);
  }

  public get poaDocument(): DocumentModel {
    return !!this.companyId
      ? this.companyDocuments.find((document: DocumentModel) => document.type === enumDocumentType.POWER_OF_ATTORNEY)
      : this.mapInMemoryFile(enumDocumentType.POWER_OF_ATTORNEY);
  }

  public get punDocument(): DocumentModel {
    return !!this.companyId
      ? this.companyDocuments.find((document: DocumentModel) => document.type === enumDocumentType.ACH_APPROVAL)
      : this.mapInMemoryFile(enumDocumentType.ACH_APPROVAL);
  }

  public get uniqueOrganizationMembers(): OrganizationMemberModel[] {
    return (this.organizationMembers ?? [])
      .filter((member: OrganizationMemberModel) => member.company?.id === this.company?.id)
      .filter((member, index, self) => {
        return index === self.findIndex((m) => m.user.userId === member.user.userId);
      });
  }

  public updateDocumentState(documentId: number, state: enumDocumentState): void {
    this.companyDocuments = this.companyDocuments.map((document: DocumentModel) =>
      document.id === documentId
        ? {
            ...document,
            state,
          }
        : document,
    );
  }

  public uploadDocument(file: File, docType: enumDocumentType): void {
    if (this.companyId) {
      this.uploadDocumentToServer(file, docType);
      return;
    }
    this.uploadDocuments.push({ docType: docType, file });
  }

  public deleteDocument(document: DocumentModel, docType: enumDocumentType): void {
    const companyId = this.companyId || document?.relatedEntityId;

    if (companyId && document?.id) {
      this.deleteDocumentFromServer(document.id, docType);
      return;
    }

    this.uploadDocuments = this.uploadDocuments.filter((file: { docType: number; file: File }) => {
      return file.docType !== docType;
    });
    this.cd.markForCheck();
  }

  public get companyAddressLine(): string {
    const address: AddressModel = this.formGroup.getRawValue()?.address;
    return [
      [address?.line1, address?.line2, address?.line3].join(' '),
      address?.city,
      [address?.state?.code, address?.postalCode].join(' '),
      address?.country?.description,
    ]
      .filter((item: string) => !!item?.trim())
      .join(', ');
  }

  public get paymentTypeText(): string {
    return this.formGroup.get('achIsProvided').value ? 'ACH_CONNECTED' : 'ACH_NOT_CONNECTED';
  }

  public get companyIdentifier(): string {
    if (this.isHasAssignedCbpRequest && !!this.company?.cbpAssignedNumberRegistration) {
      switch (this.company?.cbpAssignedNumberRegistration.status) {
        case enumCbpAssignedNumberStatus.SENT:
        case enumCbpAssignedNumberStatus.SENDING:
          return this.translateService.instant('LABELS.ASSIGNED_CBP_PENDING');
        case enumCbpAssignedNumberStatus.COMPLETED:
        default:
          return this.company?.identifier;
      }
    } else {
      return this.company?.identifier;
    }
  }

  public get isCompanyWithCbpAssignedNumberRequested(): boolean {
    return (
      this.isHasAssignedCbpRequest &&
      (this.company?.cbpAssignedNumberRegistration?.status === enumCbpAssignedNumberStatus.SENT ||
        this.company?.cbpAssignedNumberRegistration?.status === enumCbpAssignedNumberStatus.SENDING ||
        this.company?.cbpAssignedNumberRegistration?.status === enumCbpAssignedNumberStatus.COMPLETED)
    );
  }

  public get isCbpRegistrationPending(): boolean {
    return (
      this.isHasAssignedCbpRequest &&
      (this.company?.cbpAssignedNumberRegistration?.status === enumCbpAssignedNumberStatus.SENT ||
        this.company?.cbpAssignedNumberRegistration?.status === enumCbpAssignedNumberStatus.SENDING)
    );
  }

  public isCbpRegistrationCompleted(company: CompanyModel): boolean {
    return this.isHasAssignedCbpRequest && company?.cbpAssignedNumberRegistration?.status === enumCbpAssignedNumberStatus.COMPLETED;
  }

  public isCbpRegistrationRejected(company: CompanyModel): boolean {
    return this.isHasAssignedCbpRequest && company?.cbpAssignedNumberRegistration?.status === enumCbpAssignedNumberStatus.REJECTED;
  }

  public get registrationCompanyText(): string {
    switch (this.lastDependency.state) {
      case enumDependencyState.REJECTED:
        return 'LABELS.REGISTRATION_REJECTED';
      case enumDependencyState.ACCEPTED:
        return 'DEPENDENCY_STATE.ACCEPTED';
      case enumDependencyState.PROVIDER_SEND_PENDING:
        return 'LABELS.REGISTRATION_PENDING';
    }
  }

  public get registrationCompanyTextColor(): string {
    switch (this.lastDependency.state) {
      case enumDependencyState.REJECTED:
        return '#ff5c5c';
      case enumDependencyState.ACCEPTED:
        return '#27ae60';
      case enumDependencyState.PROVIDER_SEND_PENDING:
        return '#ff9500';
    }
  }

  public save(isNeedCheck: boolean = true, finishWithCbpAssignedRedirect = false): void {
    // #13189 — HARD block: a banned registered-agent address can never be saved, including via
    // the "save as is" decline path (save(false)). Takes priority over the missing-fields popup.
    if (this.isBannedAddressEntered()) {
      this.showBannedAddressModal = true;
      return;
    }

    // #13269 — the persons FormArray is excluded from the generic walk (its flat field names would
    // collide with the IOR contact keys) and reported as one aggregate entry per incomplete person
    const invalidFields: [{ [key: string]: ValidationMessageModel }] = this.fh.validateAllFields(this.formGroup, {}, [
      'controllingPersons',
    ]);
    Object.assign(invalidFields, this.getControllingPersonInvalidFields());

    if (this.employeeStatusComponent && !this.employeeStatusComponent.isEmployee) {
      const orgInvalidFields = this.employeeStatusComponent.getInvalidOrgFields();
      Object.assign(invalidFields, orgInvalidFields);
    }

    if (isNeedCheck && Object.keys(invalidFields).length > 0) {
      this.openValidationModal(invalidFields);
      return;
    }

    if (!!this.formGroup.get('achIsProvided') && !!this.formGroup.get('achIsProvided')?.value && !this.punDocument) {
      this.showPUNDocValidationModal = true;
      return;
    }

    if (
      !!this.formGroup.get('achIsProvided') &&
      !!this.formGroup.get('achIsProvided')?.value &&
      !this.formGroup.get('authorizationAgreementAccepted')?.value &&
      !this.showPUNDocValidationModal
    ) {
      this.showPUNCheckboxValidationModal = true;
      return;
    }

    // capture org data before save to prevent race condition
    const orgData =
      this.employeeStatusComponent && !this.employeeStatusComponent.isEmployee ? this.employeeStatusComponent.getOrgFormData() : null;

    this.iorMatchGate
      .confirmIorMatchOrPass()
      .pipe(
        take(1),
        tap(() => (this.isLoadingUpsert = true)),
        switchMap(() => this.updateCompany()),
        // #13269 — patch the real ids of newly created people back into the form before uploading their scans
        tap((company: CompanyModel) => this.syncControllingPersonIds(company)),
        switchMap((company: CompanyModel) => {
          this.companyId = company.id;
          this.companiesService.setDefaultCompany(company);
          if (orgData) {
            const orgAddress: AddressModel = !this.organization.address ? company.address : this.organization.address;
            return this.organizationsRepository
              .update(this.organization.id, {
                ...this.organization,
                ...orgData,
                address: orgAddress,
              })
              .pipe(map(() => company));
          }
          return of(company);
        }),
        switchMap((company: CompanyModel) => {
          return this.isRegisterCompany(company) && !finishWithCbpAssignedRedirect
            ? this.dependenciesService.updateRegisteredCompany(company, this.companiesService.getUser())
            : of({});
        }),
        switchMap(() => {
          return !!this.delayedUploadFile ? this.uploadLogo(this.delayedUploadFile) : of({});
        }),
        switchMap(() => {
          return this.uploadDocuments.length > 0 ? this.updateDocuments() : of({});
        }),
        switchMap(() => this.uploadControllingPersonScans()),
        switchMap(() => {
          return this.companiesService.getUserData(true);
        }),
        switchMap(() => {
          return this.companiesService.getCompanyAsync(true, false, this.companyId);
        }),
        tap((company: CompanyModel) => {
          this.formGroup.get('phone')?.patchValue(company?.phone);
        }),
        catchError(() => {
          return this.companiesService.getCompanyAsync(true, false, this.companyId);
        }),
        takeUntil(this.destroy$),
        // #13269 — updateCompany() swallows PUT rejections into EMPTY (complete without emission),
        // so neither subscribe callback runs; finalize releases the spinner on every outcome
        finalize(() => (this.isLoadingUpsert = false)),
      )
      .subscribe(
        () => {
          let validationStateUpdate$: Observable<any> = of({});
          if (
            this.formGroup.valid &&
            !this.companiesService.getCompanyById(this.companyId)?.validationDate &&
            this.placement !== enumCompanyPlacement.CBP_ASSIGNED_NUMBER_REQUEST &&
            !finishWithCbpAssignedRedirect
          ) {
            validationStateUpdate$ = this.companiesService.updateCompanyValidationState(this.placement).pipe(takeUntil(this.destroy$));
            this.ga.companyValidate(this.gtmEventAction, String(this.companyId));
          }
          validationStateUpdate$.subscribe(() => {
            //TODO: replace with one Emitter
            this.getCompanyDependencies();
            this.formGroup.markAsUntouched();
            // #13175 — reset dirty too: after a successful save there are no unsaved changes,
            // so re-entering edit must not pop the "unsaved changes" modal (dirty-gated).
            this.formGroup.markAsPristine();
            if (this.isEditView) {
              this.acceptEditableBlock('isEditBusinessInfo');
              this.acceptEditableBlock('isEditDutiesPayment');
            }
            this.updateCompanySource$.emit(this.formGroup);
            if (this.isHasAssignedCbpRequest && finishWithCbpAssignedRedirect) {
              this.router.navigate(['/companies', 'cbp-assigned-number-request', this.companyId, 'start']);
            } else {
              this.createCompanySource$.emit(this.companyId);
            }
            this.companiesService.companyUpdated$.next();
          });
          this.isLoadingUpsert = false;
        },
        () => (this.isLoadingUpsert = false),
      );
  }

  public onSaveBeforeDigitalSign(): void {
    // #13189 — this flow bypasses save(), so it needs the same banned-address hard block.
    if (this.isBannedAddressEntered()) {
      this.showBannedAddressModal = true;
      return;
    }

    const orgData =
      this.employeeStatusComponent && !this.employeeStatusComponent.isEmployee ? this.employeeStatusComponent.getOrgFormData() : null;

    this.iorMatchGate
      .confirmIorMatchOrPass()
      .pipe(
        take(1),
        tap(() => (this.isLoadingUpsert = true)),
        switchMap(() => this.updateCompany()),
        // #13269 — patch the real ids of newly created people back into the form before uploading their scans
        tap((company: CompanyModel) => this.syncControllingPersonIds(company)),
        switchMap((company: CompanyModel) => {
          this.companyId = company.id;
          this.companiesService.setDefaultCompany(company);
          if (orgData) {
            const orgAddress: AddressModel = !this.organization.address ? company.address : this.organization.address;
            return this.organizationsRepository
              .update(this.organization.id, {
                ...this.organization,
                ...orgData,
                address: orgAddress,
              })
              .pipe(map(() => company));
          }
          return of(company);
        }),
        switchMap((company) => {
          return this.uploadDocuments.length > 0 ? this.updateDocuments() : of({});
        }),
        switchMap(() => this.uploadControllingPersonScans()),
        switchMap(() => this.companiesService.getUserData(true)),
        takeUntil(this.destroy$),
        // #13269 — updateCompany() swallows PUT rejections into EMPTY (complete without emission),
        // so neither subscribe callback runs; finalize releases the spinner and the waiting PoA button.
        // cancelWaitingForSave after a successful proceedAfterSave is a no-op.
        finalize(() => {
          this.isLoadingUpsert = false;
          if (this.poaComponent) {
            this.poaComponent.cancelWaitingForSave();
          }
        }),
      )
      .subscribe({
        next: () => {
          this.isLoadingUpsert = false;
          this.formGroup.markAsUntouched();
          // #13175 — reset dirty too (see save() success): no unsaved changes after a successful save.
          this.formGroup.markAsPristine();
          if (this.poaComponent) {
            this.poaComponent.proceedAfterSave(this.companyId);
          }
        },
        error: () => {
          this.isLoadingUpsert = false;
          if (this.poaComponent) {
            this.poaComponent.cancelWaitingForSave();
          }
        },
      });
  }

  private get gtmEventAction(): enumGATagEventAction {
    switch (this.placement) {
      case enumCompanyPlacement.ENTRY_SUMMARY:
        return enumGATagEventAction.CompanyValidateES;
      case enumCompanyPlacement.ISF:
        return enumGATagEventAction.CompanyValidateISF;
      case enumCompanyPlacement.SHIPMENT:
        return enumGATagEventAction.CompanyValidateLCL;
      case enumCompanyPlacement.COMPANY_PAGE:
        return enumGATagEventAction.CompanyValidateCompanyPage;
      case enumCompanyPlacement.CONTINUOUS_BOND:
        return enumGATagEventAction.CompanyValidateAnnualBond;
    }
  }

  public updateLogoControl(file: File): void {
    if (!this.companyId) {
      this.delayedUploadFile = file;
      return;
    }

    this.uploadLogo(file).subscribe((company: CompanyModel) => {
      this.formGroup.get('logoUri').patchValue(company.logoUri);
      this.cd.markForCheck();
    });
  }

  public removeLogo(): void {
    if (!this.companyId) {
      this.formGroup.get('logoUri').patchValue(null);
      return;
    }
    this.companyLogoRepository
      .deleteUrl(this.companyId)
      .pipe(take(1))
      .subscribe(() => {
        this.formGroup.get('logoUri').patchValue(null);
      });
  }

  public updatePhotoIdControl(file: File): void {
    if (!this.companyId) {
      this.uploadDocuments = this.uploadDocuments.filter(
        (doc: { docType: number; file: File }) => doc.docType !== enumDocumentType.ID_DOCUMENT,
      );
      this.uploadDocuments.push({ docType: enumDocumentType.ID_DOCUMENT, file });
      return;
    }

    const deleteOld$ = this.photoIdDocument?.id ? this.documentsService.deleteDocument(this.photoIdDocument.id, false) : of(null);

    this.setDocumentIsLoading(true, enumDocumentType.ID_DOCUMENT);
    deleteOld$
      .pipe(
        switchMap(() =>
          this.documentsService.uploadDocument(
            {
              fileName: file.name,
              relatedEntityId: this.companyId,
              relatedEntityType: enumDocumentRelatedEntityType.COMPANY,
              rootEntityId: this.companyId,
              rootEntityType: enumDocumentRootEntityType.COMPANY,
              type: enumDocumentType.ID_DOCUMENT,
            },
            file,
            this.companyId,
          ),
        ),
        switchMap(() => this.getCompanyDocuments(false)),
        finalize(() => this.setDocumentIsLoading(false, enumDocumentType.ID_DOCUMENT)),
        takeUntil(this.destroy$),
      )
      .subscribe(() => this.cd.detectChanges());
  }

  // #13356 — mirrors updatePhotoIdControl: buffered until the company exists, replace-on-upload after
  public updateSs4Control(file: File): void {
    if (!this.companyId) {
      this.uploadDocuments = this.uploadDocuments.filter((doc: { docType: number; file: File }) => doc.docType !== enumDocumentType.SS4);
      this.uploadDocuments.push({ docType: enumDocumentType.SS4, file });
      return;
    }

    const deleteOld$ = this.ss4Document?.id ? this.documentsService.deleteDocument(this.ss4Document.id, false) : of(null);

    this.setDocumentIsLoading(true, enumDocumentType.SS4);
    deleteOld$
      .pipe(
        switchMap(() =>
          this.documentsService.uploadDocument(
            {
              fileName: file.name,
              relatedEntityId: this.companyId,
              relatedEntityType: enumDocumentRelatedEntityType.COMPANY,
              rootEntityId: this.companyId,
              rootEntityType: enumDocumentRootEntityType.COMPANY,
              type: enumDocumentType.SS4,
            },
            file,
            this.companyId,
          ),
        ),
        switchMap(() => this.getCompanyDocuments(false)),
        finalize(() => this.setDocumentIsLoading(false, enumDocumentType.SS4)),
        takeUntil(this.destroy$),
      )
      .subscribe(() => this.cd.detectChanges());
  }

  public uploadLogo(file: File): Observable<CompanyModel> {
    return this.companyLogoRepository.getCredentials(this.companyId).pipe(
      switchMap((credentials: DocumentAccessCredentialsModel) => {
        return this.documentsRepository.uploadFileByUrl(credentials.uploadUrl, file, false).pipe(map(() => credentials));
      }),
      switchMap((credentials) => {
        return this.companyLogoRepository.setUrl(this.companyId, credentials.downloadUrl);
      }),
      switchMap(() => this.companiesService.getCompanyAsync(true, false, this.companyId)),
      take(1),
    );
  }

  public applyForAssignedCbpNumber() {
    this.save(false, true);
  }

  private isRegisterCompany(company: CompanyModel): boolean {
    if (this.placement === enumCompanyPlacement.CBP_ASSIGNED_NUMBER_REQUEST) {
      return false;
    }

    const isFormViewValid: boolean = this.companiesService.validateCompany(this.placement, true, company);
    const isFormControlsValid: boolean = this.companiesService.validateCompany(this.placement, false, company);

    if (this.formGroup.get('isUnitedStates').value) {
      if (this.formGroup.get('identifierQualifier').value === enumIdentifierQualifier.ANI) {
        return isFormControlsValid && isFormViewValid;
      } else {
        return isFormControlsValid && isFormViewValid && this.formGroup.get('isAllowedRegistration').value;
      }
    }

    if (!this.formGroup.get('isUnitedStates').value && !this.formGroup.get('isAllowedForCustoms').value) {
      return false;
    }

    if (!this.formGroup.get('isUnitedStates').value && this.formGroup.get('isAllowedForCustoms').value) {
      return isFormControlsValid && isFormViewValid && this.formGroup.get('identifierQualifier').value === enumIdentifierQualifier.ANI;
    }
  }

  private setDocumentIsLoading(value: boolean, docType: any): void {
    let isLoadingKey: string;
    switch (docType) {
      case enumDocumentType.POWER_OF_ATTORNEY:
        isLoadingKey = 'isLoadingCompanyPoa';
        break;
      case enumDocumentType.ACH_APPROVAL:
        isLoadingKey = 'isLoadingCompanyPun';
        break;
      case enumDocumentType.ID_DOCUMENT:
        isLoadingKey = 'isLoadingPhotoId';
        break;
      case enumDocumentType.SS4:
        isLoadingKey = 'isLoadingSs4';
        break;
    }
    this[isLoadingKey] = value;
    this.cd.markForCheck();
  }

  private getCompanyDocuments(isShowLoader: boolean): Observable<DocumentModel[]> {
    return this.documentsService.getCompanyDocuments(this.companyId, isShowLoader).pipe(
      tap((documents: DocumentModel[]) => {
        this.companyDocuments = documents;
        this.loadPhotoIdFromDocuments(documents);
        // #13356 — refresh the SS-4 from the same flat list
        this.loadSs4FromDocuments(documents);
        // #13269 — refresh the controlling-person State ID scans from the same flat list
        this.loadControllingPersonScansFromDocuments(documents);
        this.cd.detectChanges();
      }),
    );
  }

  private loadPhotoIdFromDocuments(documents: DocumentModel[]): void {
    const photoIdDoc =
      (documents || []).filter((d: DocumentModel) => d.type === enumDocumentType.ID_DOCUMENT).sort((a, b) => b.id - a.id)[0] || null;

    this.photoIdDocument = photoIdDoc || null;
    this.photoIdIsImage = !!photoIdDoc && this.documentsService.checkImageFileExtension(photoIdDoc.fileName);

    if (!photoIdDoc) {
      this.photoIdDownloadUrl = null;
      return;
    }

    if (photoIdDoc.state === enumDocumentState.UPLOADED) {
      this.fetchPhotoIdDownloadUrl(photoIdDoc.id);
    } else {
      timer(0, DEFAULT_POLLING_TIMEOUT)
        .pipe(
          take(DEFAULT_MAX_POLLING_RETRY),
          switchMap(() => this.documentsService.getDocumentState(photoIdDoc.id, false)),
          filter((state: enumDocumentState) => state !== enumDocumentState.SCAN_PENDING && state !== enumDocumentState.CREATED),
          first(),
          takeUntil(this.destroy$),
        )
        .subscribe(
          (state: enumDocumentState) => {
            if (state === enumDocumentState.UPLOADED) {
              this.fetchPhotoIdDownloadUrl(photoIdDoc.id);
            }
          },
          () => {},
        );
    }
  }

  // #13356 — mirrors loadPhotoIdFromDocuments: newest SS-4 wins, poll until the scan finishes
  private loadSs4FromDocuments(documents: DocumentModel[]): void {
    const ss4Doc: DocumentModel =
      (documents || []).filter((d: DocumentModel) => d.type === enumDocumentType.SS4).sort((a, b) => b.id - a.id)[0] || null;

    this.ss4Document = ss4Doc || null;
    this.ss4IsImage = !!ss4Doc && this.documentsService.checkImageFileExtension(ss4Doc.fileName);

    if (!ss4Doc) {
      this.ss4DownloadUrl = null;
      return;
    }

    if (ss4Doc.state === enumDocumentState.UPLOADED) {
      this.fetchSs4DownloadUrl(ss4Doc.id);
    } else {
      timer(0, DEFAULT_POLLING_TIMEOUT)
        .pipe(
          take(DEFAULT_MAX_POLLING_RETRY),
          switchMap(() => this.documentsService.getDocumentState(ss4Doc.id, false)),
          filter((state: enumDocumentState) => state !== enumDocumentState.SCAN_PENDING && state !== enumDocumentState.CREATED),
          first(),
          takeUntil(this.destroy$),
        )
        .subscribe(
          (state: enumDocumentState) => {
            if (state === enumDocumentState.UPLOADED) {
              this.fetchSs4DownloadUrl(ss4Doc.id);
            }
          },
          () => {},
        );
    }
  }

  private fetchSs4DownloadUrl(docId: number): void {
    this.documentsRepository
      .getDownloadLink(docId, false)
      .pipe(take(1))
      .subscribe(
        (result: { downloadUrl: string }) => {
          this.ss4DownloadUrl = result.downloadUrl;
          this.cd.markForCheck();
        },
        () => {},
      );
  }

  // #13269 — a State ID picked in a section is only buffered; it is uploaded after "Save changes"
  // returns the person ids (a new person gets its id only from the PUT response).
  public onControllingPersonScanSelected(event: { group: UntypedFormGroup; file: File }): void {
    // The entry is stored synchronously (the PoA gate / payload filter must see the file at once);
    // the preview is a data: URL produced async — an object URL would be blocked by Angular's
    // [src] sanitizer as unsafe blob: (broken image + console errors). While previewUrl is null the
    // binding stays null, so the uploader keeps showing its own internal FileReader preview.
    const entry: ControllingPersonScanBufferEntry = {
      file: event.file,
      previewUrl: null,
      isImage: this.documentsService.checkImageFileExtension(event.file.name),
    };
    this.controllingPersonScans.set(event.group, entry);
    this.controllingPersonScanErrors.delete(event.group);
    event.group.markAsDirty();
    this.cd.detectChanges();

    const reader: FileReader = new FileReader();
    reader.onload = () => {
      // guard against a quick re-pick: only attach the preview to the entry it was read for
      if (this.controllingPersonScans.get(event.group) === entry) {
        entry.previewUrl = reader.result as string;
        this.cd.detectChanges();
      }
    };
    reader.readAsDataURL(event.file);
  }

  public onControllingPersonRemoved(group: UntypedFormGroup): void {
    this.releaseControllingPersonScan(group);
    this.controllingPersonScanErrors.delete(group);
    this.controllingPersonUploadingGroups.delete(group);
    this.cd.detectChanges();
  }

  public onControllingPersonScanRetry(group: UntypedFormGroup): void {
    const entry: ControllingPersonScanBufferEntry = this.controllingPersonScans.get(group);
    if (!entry || !(group.get('id')?.value > 0)) {
      return;
    }
    this.uploadControllingPersonScan(group, entry)
      .pipe(
        switchMap(() => this.getCompanyDocuments(false)),
        takeUntil(this.destroy$),
      )
      .subscribe(
        () => {},
        () => {},
      );
  }

  // Uploads every buffered scan whose section still exists and whose person got a real id from the PUT.
  // A failed upload never rolls the company save back — the file stays buffered and the section shows a retry.
  private uploadControllingPersonScans(): Observable<any> {
    const personsArray = this.controllingPersonsArray;
    if (!personsArray || this.controllingPersonScans.size === 0) {
      return of({});
    }
    const jobs: Array<Observable<any>> = [];
    this.controllingPersonScans.forEach((entry: ControllingPersonScanBufferEntry, group: AbstractControl) => {
      if (personsArray.controls.includes(group) && group.get('id')?.value > 0) {
        jobs.push(this.uploadControllingPersonScan(group as UntypedFormGroup, entry).pipe(catchError(() => of(null))));
      }
    });
    if (jobs.length === 0) {
      return of({});
    }
    return forkJoin(jobs).pipe(switchMap(() => this.getCompanyDocuments(false)));
  }

  private uploadControllingPersonScan(group: UntypedFormGroup, entry: ControllingPersonScanBufferEntry): Observable<any> {
    const personId: number = group.get('id').value;
    const previousDocument: DocumentModel = this.controllingPersonDocuments.find(
      (document: DocumentModel) => document.relatedEntityId === personId,
    );
    this.controllingPersonUploadingGroups.add(group);
    this.controllingPersonScanErrors.delete(group);
    return this.documentsService
      .uploadDocument(
        {
          fileName: entry.file.name,
          relatedEntityId: personId,
          relatedEntityType: enumDocumentRelatedEntityType.COMPANY_CONTROLLING_PERSON,
          rootEntityId: this.companyId,
          rootEntityType: enumDocumentRootEntityType.COMPANY,
          type: enumDocumentType.CONTROLLING_PERSON_STATE_ID,
        },
        entry.file,
        this.companyId,
      )
      .pipe(
        // DocumentsService.uploadDocument completes without emitting when the file upload itself fails
        defaultIfEmpty(null),
        map((documentId: number) => {
          if (!documentId) {
            throw new Error('controlling-person-scan-upload-failed');
          }
          return documentId;
        }),
        switchMap((documentId: number) => this.waitForDocumentUploaded(documentId)),
        // replace flow per the API contract: the new scan is uploaded first, then the old one is deleted
        switchMap(() =>
          previousDocument ? this.documentsService.deleteDocument(previousDocument.id, false).pipe(catchError(() => of(null))) : of(null),
        ),
        defaultIfEmpty(null),
        tap(() => {
          this.controllingPersonScanUrls.delete(previousDocument?.id);
          this.releaseControllingPersonScan(group);
        }),
        catchError((error) => {
          this.controllingPersonScanErrors.set(group, 'COMPANY.CONTROLLING_PERSONS.SCAN_UPLOAD_FAILED');
          throw error;
        }),
        finalize(() => {
          this.controllingPersonUploadingGroups.delete(group);
          this.cd.detectChanges();
        }),
      );
  }

  private waitForDocumentUploaded(documentId: number): Observable<enumDocumentState> {
    return timer(0, DEFAULT_POLLING_TIMEOUT).pipe(
      take(DEFAULT_MAX_POLLING_RETRY),
      switchMap(() => this.documentsService.getDocumentState(documentId, false)),
      filter((state: enumDocumentState) => state !== enumDocumentState.SCAN_PENDING && state !== enumDocumentState.CREATED),
      first(),
      tap((state: enumDocumentState) => {
        if (state !== enumDocumentState.UPLOADED) {
          throw new Error('controlling-person-scan-not-uploaded');
        }
      }),
    );
  }

  private controllingPersonHasValues(person: CompanyControllingPersonModel): boolean {
    return !!(person.firstName || person.lastName || person.socialSecurityNumber || person.isUsCitizenOrResident);
  }

  // getRawValue() keeps the FormArray order, so the payload index maps 1:1 to the control at the same position
  private hasBufferedScanAt(index: number): boolean {
    const personsArray = this.controllingPersonsArray;
    return !!personsArray && this.controllingPersonScans.has(personsArray.at(index));
  }

  // #13269 — one aggregate missing-fields entry per incomplete person. The State ID scan is not
  // a form control, so its absence is injected here (employee-status getInvalidOrgFields precedent).
  private getControllingPersonInvalidFields(): { [key: string]: ValidationMessageModel } {
    const invalidFields: { [key: string]: ValidationMessageModel } = {};
    const personsArray = this.controllingPersonsArray;
    const businessType = this.formGroup.get('businessType')?.value;
    if (!personsArray || !requiresControllingPersons(businessType)) {
      return invalidFields;
    }
    const roleKey: string = controllingPersonRoleKey(businessType, this.formGroup.get('partnershipType')?.value);
    personsArray.controls.forEach((group: AbstractControl, index: number) => {
      this.fh.markAsTouched(group);
      const missingLabels: string[] = [];
      if (group.get('firstName')?.invalid) {
        missingLabels.push(this.translateService.instant('LABELS.FIRST_NAME'));
      }
      if (group.get('lastName')?.invalid) {
        missingLabels.push(this.translateService.instant('LABELS.LAST_NAME'));
      }
      if (group.get('socialSecurityNumber')?.invalid) {
        missingLabels.push(this.translateService.instant('COMPANY.CONTROLLING_PERSONS.SSN_LABEL'));
      }
      if (group.get('isUsCitizenOrResident')?.invalid) {
        missingLabels.push(this.translateService.instant('COMPANY.CONTROLLING_PERSONS.CITIZENSHIP_CONFIRMATION'));
      }
      if (!this.hasControllingPersonScan(group)) {
        missingLabels.push(this.translateService.instant('COMPANY.CONTROLLING_PERSONS.STATE_ID'));
      }
      if (missingLabels.length > 0) {
        invalidFields[`controllingPerson${index}`] = {
          text: 'required',
          severity: enumValidationMessageSeverity.ERROR,
          labelKey: 'FORM_LABEL.CONTROLLING_PERSON_MISSING',
          keyParams: { role: this.translateService.instant(roleKey), n: index + 1, fields: missingLabels.join(', ') },
        };
      }
    });
    return invalidFields;
  }

  private hasControllingPersonScan(group: AbstractControl): boolean {
    if (this.controllingPersonScans.has(group)) {
      return true;
    }
    const personId: number = group.get('id')?.value;
    return personId > 0 && this.controllingPersonDocuments.some((document: DocumentModel) => document.relatedEntityId === personId);
  }

  private isControllingPersonsComplete(businessType: enumCompanyBusinessType): boolean {
    if (!requiresControllingPersons(businessType)) {
      return true;
    }
    const personsArray = this.controllingPersonsArray;
    if (!personsArray) {
      return true;
    }
    if (isPartnership(businessType) && !this.formGroup.get('partnershipType')?.value) {
      return false;
    }
    if (personsArray.length < minControllingPersons(businessType) || personsArray.invalid) {
      return false;
    }
    return personsArray.controls.every((group: AbstractControl) => this.hasControllingPersonScan(group));
  }

  // The PUT response carries the real ids of newly created people; the form is not rebuilt after save,
  // so the ids must be patched back — a second save with id 0 would create duplicates.
  private syncControllingPersonIds(company: CompanyModel): void {
    const personsArray = this.controllingPersonsArray;
    if (!personsArray || !company?.controllingPersons) {
      return;
    }
    const unmatched: CompanyControllingPersonModel[] = [...company.controllingPersons];
    personsArray.controls.forEach((group: AbstractControl) => {
      const storedId: number = group.get('id')?.value;
      if (storedId > 0) {
        const index: number = unmatched.findIndex((person: CompanyControllingPersonModel) => person.id === storedId);
        if (index >= 0) {
          unmatched.splice(index, 1);
        }
      }
    });
    // Response order is not contractual — new people are matched by their value triple
    // (the server trims names/SSN and turns whitespace-only into null, hence the normalization).
    personsArray.controls.forEach((group: AbstractControl) => {
      const idControl = group.get('id');
      if (!idControl || idControl.value > 0) {
        return;
      }
      const firstName: string = (group.get('firstName')?.value || '').trim();
      const lastName: string = (group.get('lastName')?.value || '').trim();
      const ssn: string = (group.get('socialSecurityNumber')?.value || '').trim();
      const index: number = unmatched.findIndex(
        (person: CompanyControllingPersonModel) =>
          (person.firstName || '') === firstName && (person.lastName || '') === lastName && (person.socialSecurityNumber || '') === ssn,
      );
      if (index >= 0) {
        idControl.patchValue(unmatched[index].id, { emitEvent: false });
        unmatched.splice(index, 1);
      }
    });
  }

  private loadControllingPersonScansFromDocuments(documents: DocumentModel[]): void {
    const scans: DocumentModel[] = (documents || [])
      .filter(
        (document: DocumentModel) =>
          document.type === enumDocumentType.CONTROLLING_PERSON_STATE_ID &&
          document.relatedEntityType === enumDocumentRelatedEntityType.COMPANY_CONTROLLING_PERSON &&
          document.state !== enumDocumentState.DELETED,
      )
      .sort((a: DocumentModel, b: DocumentModel) => b.id - a.id);

    // newest scan per person wins — the replace flow may briefly leave two documents
    const personIds: Set<number> = new Set<number>();
    this.controllingPersonDocuments = scans.filter((document: DocumentModel) => {
      if (personIds.has(document.relatedEntityId)) {
        return false;
      }
      personIds.add(document.relatedEntityId);
      return true;
    });

    this.controllingPersonDocuments.forEach((document: DocumentModel) => {
      if (this.controllingPersonScanUrls.has(document.id)) {
        return;
      }
      if (document.state === enumDocumentState.UPLOADED) {
        this.fetchControllingPersonScanUrl(document);
      } else {
        this.waitForDocumentUploaded(document.id)
          .pipe(takeUntil(this.destroy$))
          .subscribe(
            () => this.fetchControllingPersonScanUrl(document),
            () => {},
          );
      }
    });
  }

  private fetchControllingPersonScanUrl(document: DocumentModel): void {
    this.documentsRepository
      .getDownloadLink(document.id, false)
      .pipe(take(1))
      .subscribe(
        (result: { downloadUrl: string }) => {
          this.controllingPersonScanUrls.set(document.id, result.downloadUrl);
          this.cd.detectChanges();
        },
        () => {},
      );
  }

  private releaseControllingPersonScan(group: AbstractControl): void {
    this.controllingPersonScans.delete(group);
  }

  private fetchPhotoIdDownloadUrl(docId: number): void {
    this.documentsRepository
      .getDownloadLink(docId, false)
      .pipe(take(1))
      .subscribe(
        (result: { downloadUrl: string }) => {
          this.photoIdDownloadUrl = result.downloadUrl;
          this.cd.markForCheck();
        },
        () => {},
      );
  }

  private getCompanyDependencies(): void {
    this.dependenciesService
      .getCompanyDependencies(this.companyId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((dependencies: DependencyModel[]) => {
        this.lastDependency = { ...UtilsHelper.maxElementBy<DependencyModel, string>(dependencies, 'timestamp', DateHelper.getTime) };
        if (this.lastDependency?.state === enumDependencyState.REJECTED) {
          this.getDependencyLog();
        }

        if (this.lastDependency?.state === enumDependencyState.PROVIDER_SEND_PENDING) {
          this.isRegistrationInProcess = true;
          this.setRegistrationFields(false);
          this.checkRegistrationPending();
        }
        this.cd.markForCheck();
      });
  }

  private checkRegistrationPending(): void {
    this.lastDependencyState()
      .pipe(
        map((val: enumDependencyState) => {
          if ([enumDependencyState.PROVIDER_SEND_PENDING].includes(val)) {
            throw val;
          }
          return val;
        }),
        retryWhen((data) => data.pipe(delayWhen(() => timer(15000)))),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.isRegistrationInProcess = false;
        this.setRegistrationFields(true);
      });
  }

  private lastDependencyState(): Observable<enumDependencyState> {
    const companyId: number = this.companyId;
    return this.dependenciesService.getCompanyDependencies(companyId).pipe(
      map((dependencies: DependencyModel[]) => {
        const filteredDependencies: DependencyModel[] = dependencies.filter((item: DependencyModel) => item.dependantId === companyId);
        return UtilsHelper.maxElementBy<DependencyModel, string>(filteredDependencies, 'timestamp', DateHelper.getTime).state;
      }),
    );
  }

  private setRegistrationFields(val: boolean): void {
    const funcName: string = val ? 'enable' : 'disable';
    this.formGroup.get('isAllowedRegistration')[funcName]();
    this.formGroup.get('businessName')[funcName]();
    this.formGroup.get('businessType')[funcName]();
    // #13188 — lock State of Incorporation alongside the other business-info fields during pending registration.
    // Optional: ES/ISF/LCL/bonds sidebar form definitions do not include this control (#13233).
    this.formGroup.get('stateOfIncorporation')?.[funcName]();
    // #13269 — same for the partnership sub-type (optional for the same reason)
    this.formGroup.get('partnershipType')?.[funcName]();
    this.formGroup.get('identifier')[funcName]();
    this.formGroup.get('identifierQualifier')[funcName]();
    this.formGroup.get('dateOfBirth')[funcName]();
    this.formGroup.get('address')[funcName]();
  }

  private getDependencyLog(): void {
    this.dependenciesService
      .getDependencyEvents(this.lastDependency.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe((events: ManagementEntityEventModel[]) => {
        this.lastDependencyErrorCodes = flatten(events.map((event: ManagementEntityEventModel) => event.message.match(/{R.*}/gm)))
          .map((code: string) => code?.slice(1, code.length - 1))
          .map((code: string) => code?.trim())
          .filter((code: string) => !!code);
        this.lastDependencyErrorCodes = UtilsHelper.uniq<string>(this.lastDependencyErrorCodes);
      });
  }

  private updateDocuments(): Observable<any> {
    return forkJoin([...this.uploadDocuments$]).pipe(
      takeUntil(this.destroy$),
      switchMap(() => this.getCompanyDocuments(false)),
    );
  }

  private deleteDocumentFromServer(documentId: number, docType: number): void {
    this.setDocumentIsLoading(true, docType);
    this.documentsService
      .deleteDocument(documentId, false)
      .pipe(
        switchMap(() => this.getCompanyDocuments(false)),
        finalize(() => this.setDocumentIsLoading(false, docType)),
        takeUntil(this.destroy$),
      )
      .subscribe(() => this.cd.detectChanges());
  }

  private get uploadDocuments$(): Array<Observable<number>> {
    return this.uploadDocuments.map((doc: { docType: number; file: File }) => {
      return this.documentsService
        .uploadDocument(
          {
            fileName: doc.file.name,
            relatedEntityId: this.companyId,
            relatedEntityType: enumDocumentRelatedEntityType.COMPANY,
            rootEntityId: this.companyId,
            rootEntityType: enumDocumentRootEntityType.COMPANY,
            type: Number(doc.docType),
          },
          doc.file,
          this.companyId,
        )
        .pipe(finalize(() => this.setDocumentIsLoading(false, doc.docType)));
    });
  }

  private uploadDocumentToServer(file: File, docType: number): void {
    this.setDocumentIsLoading(true, docType);
    this.documentsService
      .uploadDocument(
        {
          fileName: file.name,
          relatedEntityId: this.companyId,
          relatedEntityType: enumDocumentRelatedEntityType.COMPANY,
          rootEntityId: this.companyId,
          rootEntityType: enumDocumentRootEntityType.COMPANY,
          type: Number(docType),
        },
        file,
        this.companyId,
      )
      .pipe(
        switchMap(() => this.getCompanyDocuments(false)),
        finalize(() => this.setDocumentIsLoading(false, docType)),
        takeUntil(this.destroy$),
      )
      .subscribe(() => this.cd.detectChanges());
  }

  private mapInMemoryFile(docType: enumDocumentType): DocumentModel | null {
    const file: File = this.uploadDocuments?.find((tes: { docType: number; file: File }) => tes.docType === docType)?.file;
    if (!file) {
      return null;
    }
    return {
      id: null,
      url: null,
      relatedEntityType: enumDocumentRelatedEntityType.COMPANY,
      relatedEntityId: this.companyId,
      rootEntityType: enumDocumentRootEntityType.COMPANY,
      rootEntityId: this.companyId,
      fileName: file.name,
      type: docType,
      state: enumDocumentState.UPLOADED,
    };
  }

  private updateCompany(): Observable<CompanyModel> {
    const value: CompanyModel = omit(this.formGroup.getRawValue(), [
      'abiProfileDetails',
      'achMethodDetails',
      'filerCodeDetails',
      'achIsProvided',
    ]) as CompanyModel;

    //#12171,#12172 - FE should display as if US but should be saved based on actual selected country
    const isUnitedStates = value.address.country.code === USA_COUNTRY_CODE;
    value.isUnitedStates = isUnitedStates;

    // #13269 — drop untouched seeded sections (a phantom person must not be created) and
    // normalize '' → null per the API payload rule. Kept people (id > 0) are always echoed —
    // a person absent from the array is deleted by the backend. A new row whose only content is
    // a buffered State ID must be sent too: it needs a person id for the scan to be uploaded.
    if (Array.isArray(value.controllingPersons)) {
      value.controllingPersons = value.controllingPersons
        .filter(
          (person: CompanyControllingPersonModel, index: number) =>
            person.id > 0 || this.controllingPersonHasValues(person) || this.hasBufferedScanAt(index),
        )
        .map((person: CompanyControllingPersonModel) => ({
          ...person,
          firstName: person.firstName || null,
          lastName: person.lastName || null,
          socialSecurityNumber: person.socialSecurityNumber || null,
        }));
    }

    if (value.dateOfBirth) {
      value.dateOfBirth = this.dateTimezoneService.startOfTheDay(value.dateOfBirth);
    }
    // #8393
    if (!value.isUnitedStates || (value.isUnitedStates && value.identifierQualifier === enumIdentifierQualifier.ANI)) {
      value.isAllowedRegistration = true;
    }
    const updateAuthorizationAgreement: boolean =
      !this.companiesService.getCompanyById(this.companyId)?.authorizationAgreementAccepted &&
      this.formGroup.get('authorizationAgreementAccepted')?.value;
    return this.companiesService.updateCompany(value, updateAuthorizationAgreement, this.organization?.id).pipe(
      catchError((error: HttpErrorResponse) => {
        this.companiesService.clearStateCompany();
        if (error.status >= 400 && error.status < 500) {
          // this.alertService.error('LABELS.SOMETHING_DONT_WORK_MESSAGE');
        }
        return EMPTY;
      }),
    );
  }

  private isUsaSubscription(): void {
    this.formGroup
      .get('isUnitedStates')
      .valueChanges.pipe(takeUntil(this.formDestroy$))
      .subscribe((state: boolean) => {
        this.formGroup.get(['address', 'country']).patchValue(null);
        this.formGroup.get(['identifier']).patchValue(null);
        this.formGroup.get(['businessType']).patchValue(null);
        if (!state) {
          this.patchWhenEmptyState();
        } else {
          this.formGroup.get('businessType').setValidators([Validators.required]);
          this.formGroup.get('identifier').setValidators([Validators.required]);
          this.formGroup.get(['identifierQualifier']).patchValue(enumIdentifierQualifier.EIN);
        }
        this.formGroup.updateValueAndValidity();
      });
  }

  private isAllowCustomsSubscription(): void {
    this.formGroup
      .get('isAllowedForCustoms')
      .valueChanges.pipe(takeUntil(this.formDestroy$))
      .subscribe((state: boolean) => {
        this.formGroup.get(['address', 'country']).patchValue(null);
        this.formGroup.get(['identifier']).patchValue(null);
        this.formGroup.get(['businessType']).patchValue(null);
        if (!state) {
          this.patchWhenEmptyState();
        } else {
          this.formGroup.get(['identifierQualifier']).patchValue(enumIdentifierQualifier.ANI);
          this.formGroup.get('businessType').setValidators([Validators.required]);
          this.formGroup.get('identifier').setValidators([Validators.required]);
        }
      });
  }

  // #13188 — react to business type changes to (un)require State of Incorporation
  // #13269 — and to reshape the controlling persons requirements
  private businessTypeSubscription(): void {
    this.formGroup
      .get('businessType')
      .valueChanges.pipe(takeUntil(this.formDestroy$))
      .subscribe(() => {
        this.applyStateOfIncorporationValidator();
        this.applyControllingPersonRules();
      });
  }

  // #13520 — the State of Incorporation applies only to a US address; the country control is
  // guarded because the sidebar placements build their forms from reduced key sets
  private addressCountrySubscription(): void {
    const countryControl: AbstractControl | null = this.formGroup.get(['address', 'country']);
    if (!countryControl) {
      return;
    }
    countryControl.valueChanges.pipe(takeUntil(this.formDestroy$)).subscribe(() => this.applyStateOfIncorporationValidator());
  }

  // #13269 — the controlling persons FormArray exists only on the company-page placement
  public get controllingPersonsArray(): UntypedFormArray | null {
    return (this.formGroup.get('controllingPersons') as UntypedFormArray) || null;
  }

  // #13269 — seed the minimum sections and keep validators in sync with the business type.
  // Sections are only added, never auto-removed: switching the type must keep entered people,
  // and for Sole Proprietor / Individual the hidden sections keep their values with validators off.
  private applyControllingPersonRules(): void {
    const personsArray = this.controllingPersonsArray;
    if (!personsArray) {
      return;
    }
    const businessType = this.formGroup.get('businessType')?.value;
    const isRequired = requiresControllingPersons(businessType);

    // The minimum differs per type (Partnership 2, Corporation/LLC 1): when it shrinks after a
    // type switch, EMPTY leftover sections are removed from the end — otherwise Corporation/LLC
    // shows two seeded "beneficial owner" sections after a visit to Partnership. Sections with
    // any entered data, a buffered scan or a saved person (id > 0) are never removed automatically.
    for (let i = personsArray.length - 1; i >= 0 && personsArray.length > minControllingPersons(businessType); i--) {
      if (this.isEmptyNewControllingPerson(personsArray.at(i) as UntypedFormGroup)) {
        personsArray.removeAt(i);
      }
    }
    while (personsArray.length < minControllingPersons(businessType)) {
      personsArray.push(createControllingPersonFormGroup());
    }
    personsArray.controls.forEach((group: AbstractControl) => this.setControllingPersonValidators(group as UntypedFormGroup, isRequired));

    const partnershipTypeControl = this.formGroup.get('partnershipType');
    if (partnershipTypeControl) {
      if (isPartnership(businessType)) {
        // GP is preselected by default; a previously chosen sub-type is never overridden
        if (!partnershipTypeControl.value) {
          partnershipTypeControl.patchValue(enumCompanyPartnershipType.GENERAL_PARTNERSHIP, { emitEvent: false });
        }
        partnershipTypeControl.setValidators([Validators.required]);
      } else {
        partnershipTypeControl.clearValidators();
      }
      partnershipTypeControl.updateValueAndValidity({ emitEvent: false });
    }
  }

  // A seeded section that was never touched: no field values, no buffered scan, not a saved person
  private isEmptyNewControllingPerson(group: UntypedFormGroup): boolean {
    return (
      !(group.get('id')?.value > 0) &&
      !group.get('firstName')?.value &&
      !group.get('lastName')?.value &&
      !group.get('socialSecurityNumber')?.value &&
      !group.get('isUsCitizenOrResident')?.value &&
      !this.controllingPersonScans.has(group)
    );
  }

  private setControllingPersonValidators(group: UntypedFormGroup, isRequired: boolean): void {
    group.get('firstName').setValidators(isRequired ? [Validators.required] : null);
    group.get('lastName').setValidators(isRequired ? [Validators.required] : null);
    group.get('socialSecurityNumber').setValidators(isRequired ? [Validators.required, identifierSSNValidator()] : null);
    group.get('isUsCitizenOrResident').setValidators(isRequired ? [Validators.requiredTrue] : null);
    Object.keys(group.controls).forEach((key: string) => group.get(key).updateValueAndValidity({ emitEvent: false }));
    group.updateValueAndValidity({ emitEvent: false });
  }

  // #13188 — State of Incorporation is mandatory for US Corporation/LLC/Partnership; cleared otherwise.
  // #13520 — "US" means the flag AND the address country: a foreign ANI importer never needs it.
  private applyStateOfIncorporationValidator(): void {
    const control = this.formGroup.get('stateOfIncorporation');
    if (!control) {
      return;
    }
    const isUsa = this.formGroup.get('isUnitedStates')?.value;
    const businessType = this.formGroup.get('businessType')?.value;
    const countryControl: AbstractControl | null = this.formGroup.get(['address', 'country']);

    if (isStateOfIncorporationApplicable(isUsa, countryControl ? countryControl.value : null, businessType)) {
      control.setValidators([Validators.required]);
    } else {
      control.clearValidators();
      control.patchValue(null);
    }
    control.updateValueAndValidity();
  }

  private applyInitialValidators(): void {
    const isUsa = this.formGroup.get('isUnitedStates')?.value;
    const isAllowedForCustoms = this.formGroup.get('isAllowedForCustoms')?.value;

    if (isUsa || isAllowedForCustoms) {
      this.formGroup.get('businessType').setValidators([Validators.required]);
      this.formGroup.get('identifier').setValidators([Validators.required]);
      this.formGroup.updateValueAndValidity();
    }
  }

  //TODO: remove all code after new bonds
  public ngOnChanges(simpleChanges: SimpleChanges): void {
    if (simpleChanges?.company?.currentValue) {
      this.getBonds();
      this.pingBondsStatus$.next(true);
    }
    // #13282 — the parent rebuilds the form on "Cancel"; rebind everything to the new instance
    if (!!simpleChanges?.formGroup && !simpleChanges.formGroup.firstChange) {
      this.initFormBoundLogic();
    }
  }

  public get showPurchaseContinuousBondButton(): boolean {
    return (
      !this.company?.bondRequest || [enumBondRequestState.COMPLETED, enumBondRequestState.CUSTOM].includes(this.company?.bondRequest?.state)
    );
  }

  public get validBondLength(): number {
    return this.singleBonds?.length + this.continuousBonds?.length;
  }

  public get showAllowCustomsInEditMode(): boolean {
    return (
      this.placement === this.enumPlacement.COMPANY_PAGE &&
      !this.formGroup.controls.isUnitedStates.value &&
      !this.isHasOnboardingRedesign &&
      this.editableBlocksState.isEditBusinessInfo
    );
  }

  public updateCompanyBonds() {
    return this.companyBondsRepository
      .updateCompanyBonds(this.company?.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.getBonds();
      });
  }

  public deleteBondRequest(): void {
    this.companyBondsRepository
      .disconnectBondRequest(this.company.id)
      .pipe(
        switchMap(() => this.bondRequestRepository.delete(this.company?.bondRequest?.id)),
        switchMap(() => this.companiesService.getCompanyAsync(true)),
        take(1),
      )
      .subscribe();
  }

  public loadMonthlyBills(): void {
    this.loadMonthlyBillsCall()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: PaginatedResultModel<BillModel>) => {
        this.monthlyBillList = (result?.items || []).filter(
          (item: BillModel) => [enumPaymentState.DELAYED].includes(item.state) && !!item.company,
        );
        this.cd.markForCheck();
      });
  }

  public payMonthlyBill(billId: number): void {
    switch (this.currentCompanyPaymentMethod?.provider) {
      case enumPaymentProvider.STRIPE:
        this.companiesService
          .companyPayBill(billId, null, { companyId: this.company?.id })
          .pipe(takeUntil(this.destroy$))
          .subscribe((result: PaginatedResultModel<BillModel>) => {
            this.monthlyBillList = (result?.items || []).filter(
              (item: BillModel) => [enumPaymentState.DELAYED].includes(item.state) && !!item.company,
            );
            this.cd.markForCheck();
          });
        break;
      case enumPaymentProvider.PAYPAL:
        this.paymentsRepository
          .create(billId, true)
          .pipe(
            switchMap(() => this.loadMonthlyBillsCall()),
            takeUntil(this.destroy$),
          )
          .subscribe((result) => {
            this.monthlyBillList = (result?.items || []).filter(
              (item: BillModel) => [enumPaymentState.DELAYED].includes(item.state) && !!item.company,
            );
            this.cd.markForCheck();
          });
        break;
      default:
        break;
    }
  }

  public get isHasPayPalFastCheckout(): boolean {
    return this.companiesService
      .getUser()
      ?.betaFlags?.map((flag: UserBetaFlagModel) => flag.tag)
      .includes(enumUserBetaFlag.paypalFastCheckout);
  }

  private get isHasAssignedCbpRequest(): boolean {
    return this.companiesService
      .getUser()
      ?.betaFlags?.map((flag: UserBetaFlagModel) => flag.tag)
      .includes(enumUserBetaFlag.assignedCbpRequest);
  }

  public get currentCompanyPaymentMethod(): PaymentMethodModel {
    return (this.paymentMethods || []).find((item: PaymentMethodModel) => item.id === this.company.defaultPaymentMethodId);
  }

  public loadMonthlyBillsCall(): Observable<PaginatedResultModel<BillModel>> {
    if (!this.company?.id) {
      return of(null);
    }

    return this.billsRepository.search(this.company.id, { type: enumCompanyPaymentType.MONTHLY, companyId: this.company.id }, true);
  }

  private loadPaymentMethods(): void {
    this.isLoadingPaymentMethods = true;

    this.billsRepository
      .paymentMethods(this.company?.id)
      .pipe(
        finalize(() => (this.isLoadingPaymentMethods = false)),
        takeUntil(this.destroy$),
      )
      .subscribe((paymentMethods: PaymentMethodModel[]) => {
        const skipMethods: number[] = [5 /*alipay*/];
        this.paymentMethods = paymentMethods.filter(
          (item: PaymentMethodModel) =>
            (item.provider === enumPaymentProvider.STRIPE ||
              (this.isHasPayPalFastCheckout && item.provider === enumPaymentProvider.PAYPAL)) &&
            !skipMethods.includes(item.codeId),
        ); // atm only stripe & paypal
        this.allPaymentMethods = paymentMethods;
      });
  }

  private getBonds(isShowLoader: boolean = true): void {
    this.companyBondsRepository
      .get(this.company.id, false, isShowLoader)
      .pipe(takeUntil(this.destroy$))
      .subscribe((bonds: CompanyBondModel[]) => {
        this.singleBonds = bonds.filter((bond: CompanyBondModel) => bond.type === enumBondType.SINGLE_TRANSACTION && bond.active);
        this.continuousBonds = bonds.filter((bond: CompanyBondModel) => bond.type === enumBondType.CONTINUOUS && bond.active);
        this.cd.detectChanges();
      });
  }

  private patchWhenEmptyState(): void {
    this.formGroup.get('businessType').setValidators(null);
    this.formGroup.get('identifier').setValidators(null);
    this.formGroup.get('identifierQualifier').setValidators(null);
    ['businessType', 'identifier', 'identifierQualifier'].forEach((key) => {
      this.formGroup.get(key).setErrors(null);
    });
    this.formGroup.get(['identifierQualifier']).patchValue(null);
  }

  private openValidationModal(data: [{ [key: string]: ValidationMessageModel }]): void {
    let modalData = this.getDataWithTranslationMismatchingFieldsCorrected(data);
    this.missingFieldModal.openCustomModal(modalData);
  }

  // #13189 — banned registered-agent address entered (see @constants/banned-addresses.const).
  private isBannedAddressEntered(): boolean {
    const addressGroup: AbstractControl | null = this.formGroup.get('address');
    return addressGroup !== null && isBannedIorAddress((addressGroup as UntypedFormGroup).getRawValue());
  }

  private getDataWithTranslationMismatchingFieldsCorrected(data: [{ [key: string]: ValidationMessageModel }]) {
    const mistmatchingFieldNames = ['businessName', 'businessType', 'isAllowedRegistration', 'stateOfIncorporation', 'partnershipType'];
    let modalData = {
      ...data,
    };

    mistmatchingFieldNames.forEach((fieldName) => {
      const isFieldKeyExist = fieldName in data;

      if (isFieldKeyExist) {
        const snakeConvertedField = UtilsHelper.convertToSnakeCase(fieldName);
        modalData[snakeConvertedField] = data[fieldName];
        delete modalData[fieldName];
      }
    });

    return modalData;
  }

  private removeQueryParams(paramsToRemove: string[]) {
    const queryParams = { ...this.activeRoute.snapshot.queryParams };

    paramsToRemove.forEach((param) => {
      delete queryParams[param];
    });

    this.router.navigate([], { queryParams });
  }
}
